import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  CategorySelection,
  LocationAPIModel,
  TaskTypeAPIModel,
  UserAPIModel
} from '../../../models/api.model'
import { GlobalConstants } from '../../../constants/global-constants'
import { Subscription } from 'rxjs'
import { CommonService } from '../../../services/common/common.service'
import { DocumentService } from '../../../services/common/document.service'
import { InitializeService } from '../../../services/initialize/initialize.service'
import { Store } from '@ngrx/store'
import { AppState } from '../../../store/state/app.state'
import { GovernmentBusinessService } from '../../../services/government/government-business.service'
import { addFormControl } from '../../../utils/form-control.util'
import { getUserFromState } from '../../../store/selector/initialize.selector'

@Component({
  selector: 'create-government-business',
  templateUrl: './create-government-business.component.html',
  styleUrls: ['./create-government-business.component.scss']
})
export class CreateGovernmentBusinessComponent {
  createGovernmentBusinessForm: FormGroup
  filesToBeUploaded: FileList

  governmentBusinessTaskTypesForCategory: TaskTypeAPIModel[] = []
  governmentBusinessTaskTypesForTask = []

  selectTaskTypesSubscription$: Subscription
  user$: Subscription
  user: UserAPIModel
  selectedTaskType: TaskTypeAPIModel
  locationMap: Map<string, LocationAPIModel[]>
  locationOptions: Map<string, string>

  readonly userType = GlobalConstants.USER_TYPE
  readonly NAME_CONTROL_NAME = 'name'
  readonly GOV_BUSINESS_TASK_TYPE_CONTROL_NAME = 'type'
  readonly ASSIGNED_TO_CONTROL_NAME = 'assignedTo'
  readonly TASK_CATEGORY_CONTROL_NAME = 'taskCategory'
  readonly TASK_SUB_CATEGORY_CONTROL_NAME = 'taskSubCategory'
  readonly TASK_CODE_CONTROL_NAME = 'taskCode'
  readonly module = GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK
  readonly LOCATION = 'location'

  constructor(
    private formBuilder: FormBuilder,
    private governmentBusinessService: GovernmentBusinessService,
    private cs: CommonService,
    private documentService: DocumentService,
    private initializeService: InitializeService,
    private appState: Store<AppState>
  ) {}

  addAssignedToForm = addFormControl(this.ASSIGNED_TO_CONTROL_NAME, '')

  ngOnInit() {
    this.buildCreateGovernmentBusinessForm()
    this.populateOptions()
    this.addAssignedToForm(this.createGovernmentBusinessForm)
  }

  buildCreateGovernmentBusinessForm() {
    this.createGovernmentBusinessForm = this.formBuilder.group({
      [this.NAME_CONTROL_NAME]: ['', [Validators.required]],
      department: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(30)]],
      office: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(30)]],
      [this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME]: ['', [Validators.required]],
      sectionReference: ['', [Validators.minLength(5), Validators.maxLength(40)]],
      responseDueDatetime: [null],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5000)]],
      companyEntityId: ['', [Validators.required]],
      addToCalendar: [false],
      [this.TASK_CODE_CONTROL_NAME]: [''],
      [this.TASK_CATEGORY_CONTROL_NAME]: [''],
      [this.TASK_SUB_CATEGORY_CONTROL_NAME]: [''],
      location: ['', [Validators.required]]
    })
  }

  onCategorySelected(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection
    this.disableGovernmentBusinessTaskTypeControl('')
    this.createGovernmentBusinessForm.controls[this.TASK_CATEGORY_CONTROL_NAME]?.setValue(selectedCategory)
    this.createGovernmentBusinessForm.controls[this.TASK_SUB_CATEGORY_CONTROL_NAME]?.setValue(selectedSubCategory)
    this.createGovernmentBusinessForm.controls[this.NAME_CONTROL_NAME].enable()

    this.createGovernmentBusinessForm.get(this.NAME_CONTROL_NAME).markAsUntouched()
    this.createGovernmentBusinessForm.get(this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME).markAsUntouched()

    this.createGovernmentBusinessForm.get(this.NAME_CONTROL_NAME).setValue('')
    this.createGovernmentBusinessForm.get(this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME).setValue('')

    this.governmentBusinessTaskTypesForCategory = []
    this.governmentBusinessTaskTypesForTask = []
    this.getTaskNameAndTypeForCategory(categorySelection)
  }

  get f() {
    return this.createGovernmentBusinessForm.controls
  }

  private getTaskNameAndTypeForCategory(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.governmentBusinessTaskTypesForCategory = taskTypes
        }
      })
  }

  onTaskNameSelectChange(selectedValue: any) {
    const selectedTaskType = this.governmentBusinessTaskTypesForCategory.find(
      taskType => taskType.code === selectedValue
    )
    this.selectedTaskType = selectedTaskType

    this.createGovernmentBusinessForm.controls[this.TASK_CODE_CONTROL_NAME]?.setValue(selectedTaskType.code)

    if (selectedTaskType?.offlineTask && selectedTaskType?.onlineTask) {
      this.setTaskTypesAndEnableControl([GlobalConstants.TASK_TYPE.ONLINE, GlobalConstants.TASK_TYPE.OFFLINE])
    } else if (selectedTaskType?.onlineTask) {
      this.setTaskTypesAndDisableControl(GlobalConstants.TASK_TYPE.ONLINE)
    } else if (selectedTaskType?.offlineTask) {
      this.setTaskTypesAndDisableControl(GlobalConstants.TASK_TYPE.OFFLINE)
    }
  }

  saveAndExit() {
    const fg = this.createGovernmentBusinessForm
    if (fg.invalid) {
      fg.markAllAsTouched()
      return
    }
    const status = this.selectedTaskType?.statusList?.find(status => status?.defaultStatus)?.code

    if (!status) {
      return this.cs.showError('Status code not found')
    }
    const governmentBusiness = {
      ...fg.getRawValue(),
      name: this.selectedTaskType.label,
      status
    }

    if (!fg.value.responseDueDatetime) {
      governmentBusiness.responseDueInDays = 90
    }

    const files: File[] = Array.from(this.filesToBeUploaded ?? [])

    this.governmentBusinessService.dispatchSaveGovernmentBusinessAction(
      governmentBusiness,
      files,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.GOVERNMENT_BUSINESS, GlobalConstants.ROUTING.DASHBOARD])
    )
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.GOVERNMENT_BUSINESS, GlobalConstants.ROUTING.DASHBOARD)
  }

  private setTaskTypesAndEnableControl(types: string[]) {
    this.governmentBusinessTaskTypesForTask = types
    this.enableGovernmentBusinessTaskTypeControl()
  }

  private setTaskTypesAndDisableControl(type: string) {
    this.governmentBusinessTaskTypesForTask = [type]
    this.disableGovernmentBusinessTaskTypeControl(type)
  }

  private enableGovernmentBusinessTaskTypeControl() {
    this.createGovernmentBusinessForm.controls[this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME].enable()
  }

  private disableGovernmentBusinessTaskTypeControl(taskType: string) {
    this.createGovernmentBusinessForm.controls[this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME].setValue(taskType)
    this.createGovernmentBusinessForm.controls[this.GOV_BUSINESS_TASK_TYPE_CONTROL_NAME].disable()
  }

  handleAssignToSelectedValue(assignedTo: string) {
    this.createGovernmentBusinessForm.patchValue({ assignedTo })
  }

  handleCompanySelectedValue(companyEntityId: string) {
    this.createGovernmentBusinessForm.patchValue({ companyEntityId })
    this.loadLocationsForCompany(companyEntityId)
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  populateOptions() {
    this.initializeService.getRelatedLocationMap().subscribe({
      next: locationMap => {
        if (locationMap?.size > 0) {
          this.locationMap = locationMap
        }
      }
    })
  }

  loadLocationsForCompany(companyEntityId: string) {
    if (companyEntityId && this.locationMap?.size > 0) {
      const locationAPIModelArr: LocationAPIModel[] = this.locationMap.get(companyEntityId)
      if (locationAPIModelArr) {
        const locationMap = new Map<string, string>()
        locationAPIModelArr?.forEach(locationAPIModel => {
          locationMap.set(locationAPIModel.code, locationAPIModel.code)
        })
        this.locationOptions = locationMap
      }
    }
    const locationControl = this.createGovernmentBusinessForm.get(this.LOCATION)
    locationControl?.setValue('')
    locationControl?.updateValueAndValidity()
  }

  ngOnDestroy() {
    this.selectTaskTypesSubscription$?.unsubscribe()
  }
}
